import type { TotpEnrolStart } from '@fruuit/shared'
import { Badge, Button, Callout, Dialog, DialogCancel, Input, useToast } from '@fruuit/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Copy, KeyRound, ShieldCheck, ShieldOff } from 'lucide-react'
import QRCode from 'qrcode'
import { type FormEvent, useEffect, useState } from 'react'
import { errorMessage } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { meQueryKey } from '../../api/session'
import { useSession } from '../../auth/session-context'
import { confirmTotp, disableTotp, startTotpEnrolment } from './api'
import { formatDate } from './format'
import { StepUpDialog } from './StepUp'

/** Render the otpauth URI as an SVG data URL (CSP allows `img-src data:`). */
function useQrDataUrl(uri: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    if (!uri) {
      setUrl(null)
      return
    }
    QRCode.toString(uri, { type: 'svg', margin: 1, color: { dark: '#1F2727', light: '#FFFFFF' } })
      .then((svg) => {
        if (live) setUrl(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`)
      })
      .catch(() => {
        if (live) setUrl(null)
      })
    return () => {
      live = false
    }
  }, [uri])
  return url
}

function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim()
}

/** TOTP enrolment, recovery codes, step-up and removal (A-07, P-ID-012). */
export function MfaSettings() {
  const me = useSession()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [enrolment, setEnrolment] = useState<TotpEnrolStart | null>(null)
  const [code, setCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [disableOpen, setDisableOpen] = useState(false)
  const [disableCode, setDisableCode] = useState('')
  const [stepUpOpen, setStepUpOpen] = useState(false)
  const qr = useQrDataUrl(enrolment?.otpauth_uri ?? null)
  const refreshMe = () => queryClient.invalidateQueries({ queryKey: meQueryKey })

  const start = useApiMutation(startTotpEnrolment, { onSuccess: (data) => setEnrolment(data) })
  const confirm = useApiMutation(confirmTotp, {
    onSuccess: async (data) => {
      setEnrolment(null)
      setCode('')
      setRecoveryCodes(data.recovery_codes)
      await refreshMe()
    },
  })
  const disable = useApiMutation(disableTotp, {
    onSuccess: async () => {
      setDisableOpen(false)
      setDisableCode('')
      await refreshMe()
      toast({ tone: 'success', message: 'Two-factor authentication is off.' })
    },
  })

  const impersonated = me.impersonated_by !== null
  const { mfa } = me

  if (impersonated) {
    return (
      <Callout tone="note" title="Locked while viewing as this user">
        Two-factor settings can only be changed by the account holder.
      </Callout>
    )
  }

  async function copyCodes() {
    if (!recoveryCodes) return
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'))
      toast({ tone: 'success', message: 'Recovery codes copied.' })
    } catch {
      toast({ tone: 'error', message: 'Copy failed. Select the codes and copy them by hand.' })
    }
  }

  if (recoveryCodes) {
    return (
      <div className="flex flex-col gap-4">
        <Callout tone="info" title="Save your recovery codes">
          Each code signs you in once if you lose your authenticator. They won't be shown again.
        </Callout>
        <ul
          aria-label="Recovery codes"
          className="grid grid-cols-2 gap-2 rounded-lg border border-line bg-cream p-4 font-mono text-sm text-ink"
        >
          {recoveryCodes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" icon={<Copy />} onClick={() => void copyCodes()}>
            Copy codes
          </Button>
          <Button onClick={() => setRecoveryCodes(null)}>I've saved them</Button>
        </div>
      </div>
    )
  }

  if (enrolment) {
    const onConfirm = (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      confirm.mutate({ code })
    }
    return (
      <div className="grid gap-6 sm:grid-cols-[auto_minmax(0,1fr)]">
        <div className="flex size-44 items-center justify-center rounded-lg border border-line bg-white p-2">
          {qr ? (
            <img src={qr} alt="QR code for your authenticator app" className="size-40" />
          ) : (
            <span className="text-xs text-sage">Preparing QR code…</span>
          )}
        </div>
        <form onSubmit={onConfirm} className="flex min-w-0 flex-col gap-4">
          <ol className="list-decimal space-y-1.5 pl-5 text-sm text-ink">
            <li>
              Scan the QR code with Microsoft Authenticator, Google Authenticator or 1Password.
            </li>
            <li>Enter the 6-digit code it shows.</li>
          </ol>
          <p className="text-xs text-sage">
            Can't scan? Enter this key:{' '}
            <code className="break-all font-mono text-[13px] text-ink">
              {groupSecret(enrolment.secret)}
            </code>
          </p>
          <Input
            label="6-digit code"
            name="totp"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            inputClassName="font-mono tracking-[0.3em]"
            error={confirm.error ? errorMessage(confirm.error) : undefined}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" loading={confirm.isPending} icon={<ShieldCheck />}>
              Turn on
            </Button>
            <Button variant="ghost" onClick={() => setEnrolment(null)}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-semibold text-ink">
            Authenticator app
            {mfa.enrolled ? <Badge tone="positive">On</Badge> : <Badge tone="quiet">Off</Badge>}
          </p>
          <p className="mt-1 text-sm text-sage">
            {mfa.enrolled
              ? `Set up ${formatDate(mfa.enrolled_at)} · ${mfa.recovery_codes_remaining} recovery codes left`
              : 'Adds a 6-digit code from your phone to sensitive actions.'}
          </p>
        </div>
        {mfa.enrolled ? (
          <div className="flex flex-wrap gap-2">
            {mfa.verified_at === null ? (
              <Button
                variant="secondary"
                size="sm"
                icon={<ShieldCheck />}
                onClick={() => setStepUpOpen(true)}
              >
                Verify this session
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              icon={<ShieldOff />}
              onClick={() => setDisableOpen(true)}
            >
              Turn off
            </Button>
          </div>
        ) : (
          <Button
            icon={<KeyRound />}
            loading={start.isPending}
            onClick={() => start.mutate(undefined)}
          >
            Set up
          </Button>
        )}
      </div>
      {mfa.required && !mfa.enrolled ? (
        <Callout tone="warning">
          Your role requires two-factor authentication for administrative and review actions.
        </Callout>
      ) : null}
      {start.error ? (
        <Callout tone="danger" role="alert">
          {errorMessage(start.error)}
        </Callout>
      ) : null}
      {mfa.verified_at ? (
        <p className="text-xs text-sage">This session was verified with a second factor.</p>
      ) : null}

      <StepUpDialog
        open={stepUpOpen}
        onClose={() => setStepUpOpen(false)}
        onVerified={() => setStepUpOpen(false)}
      />
      <Dialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        size="sm"
        title="Turn off two-factor authentication?"
        description="Enter a current code from your app, or a recovery code."
        footer={
          <>
            <DialogCancel />
            <Button variant="danger" type="submit" form="mfa-disable" loading={disable.isPending}>
              Turn off
            </Button>
          </>
        }
      >
        <form
          id="mfa-disable"
          onSubmit={(event) => {
            event.preventDefault()
            disable.mutate({ code: disableCode })
          }}
        >
          <Input
            label="Authentication code"
            name="disable-code"
            required
            value={disableCode}
            onChange={(event) => setDisableCode(event.target.value)}
            error={disable.error ? errorMessage(disable.error) : undefined}
          />
        </form>
      </Dialog>
    </div>
  )
}
